import { gql, useMutation } from '@apollo/client'
import { flatten, isEmpty, pipe } from 'ramda'
import { useTranslation } from 'react-i18next'

import { cleanRepliesForApi } from '../../../../utils/helpers/cleanRepliesRequest'
import { formatApiDate } from '../../../../utils/helpers/date'
import { generalClient, serviceOrdersClient } from '../../../../utils/apollo/api'
import { useAlertModal } from '../../../../utils/routes/modals/modalsActions'
import { useExecutionForm } from '../_form/ExecutionFormHandler'
import { usePermalinkToken } from '../../../../utils/session/permalinkToken'
import { useIsBulkExecution, useServiceOrder } from '../_load/ServiceOrderLoader'
import { useNotification } from '@btime/react-messager'
import { useImperativeComponents } from '../../../../containers/dynamic/form/replies/context/ImperativeComponentsContext'
import { useBulkGridPayload } from '../bulk/_state/BulkGridStateContext'
import {
  handleStockErrors,
  indexOfFirstInvalidReply,
  indexOfFirstInvalidStockReply
} from '../_helpers/saveServiceOrder'

const MUTATION = gql`
  mutation UpdateServiceOrder($input: ServiceOrderInput, $token: String, $id: Int) {
    updateServiceOrder(id: $id, input: $input, token: $token) {
      id
    }
  }
`

// Mesma lógica de escrita da atividade individual, só que aplicada a todas as
// atividades selecionadas de uma vez (o backend reaproveita, atividade por
// atividade, a mesma validação/conclusão/automação já usada na edição
// individual). Cada atividade leva sua própria entrada (`entries`), com o
// conjunto final de respostas daquela linha da grade.
const BULK_MUTATION = gql`
  mutation BulkExecuteServiceOrders($input: BulkServiceOrderInput) {
    bulkExecuteServiceOrders(input: $input) {
      successful {
        id
      }
      failures {
        serviceOrderId
        message
      }
    }
  }
`

const cleanRepliesRequest = pipe(
  flatten, //
  cleanRepliesForApi,
  list => (isEmpty(list) ? undefined : list)
)

const cleanEventsRequest = (events, newStatusId) => {
  if (!newStatusId) return undefined
  return [...events, { statusId: newStatusId, eventDate: formatApiDate(new Date()) }]
}

export const useSaveReplies = ({ onSuccess }) => {
  const { t } = useTranslation('serviceOrders')
  const serviceOrder = useServiceOrder()
  const token = usePermalinkToken()
  const notify = useNotification()
  const { getRepliesValue } = useImperativeComponents()

  const [request, state] = useMutation(MUTATION, {
    refetchQueries: ['ServiceOrders', 'ServiceOrder', 'ServiceOrdersCount'],
    client: serviceOrdersClient,
    onCompleted: () => {
      generalClient.refetchQueries({ include: ['CalendarEvents'] })
    }
  })

  const handleRequest = () => {
    const replies = getRepliesValue()

    const input = {
      replies: cleanRepliesRequest(replies)
    }

    return request({ variables: { id: serviceOrder.id, input, token } }).then(resp => {
      if (resp.errors) {
        return notify.error({ title: t('execution.saveError') })
      }
      onSuccess()
    })
  }
  return [handleRequest, state]
}

const describeBulkFailures = failures =>
  (failures || []).map(failure => `Atividade #${failure.serviceOrderId}: ${failure.message}`).join('; ')

export const useSaveServiceOrder = ({ newStatusId, shouldValidate, onSuccess }) => {
  const { t } = useTranslation('serviceOrders')
  const serviceOrder = useServiceOrder()
  const isBulk = useIsBulkExecution()
  const form = useExecutionForm()

  const { getRepliesValue, setFocus } = useImperativeComponents()

  const [request, state] = useMutation(MUTATION, {
    refetchQueries: ['ServiceOrders', 'ServiceOrder', 'ServiceOrdersCount'],
    client: serviceOrdersClient,
    onCompleted: () => {
      generalClient.refetchQueries({ include: ['CalendarEvents'] })
    }
  })

  const [bulkRequest, bulkState] = useMutation(BULK_MUTATION, {
    refetchQueries: ['ServiceOrders', 'ServiceOrder', 'ServiceOrdersCount'],
    client: serviceOrdersClient,
    onCompleted: () => {
      generalClient.refetchQueries({ include: ['CalendarEvents'] })
    }
  })

  const openAlert = useAlertModal()
  const notify = useNotification()
  const token = usePermalinkToken()
  const getBulkPayload = useBulkGridPayload()

  const handleBulkRequest = ({ entries, events }) => {
    return bulkRequest({ variables: { input: { entries, events } } }).then(resp => {
      if (resp.errors) {
        return openAlert(t('execution.sendError'), t('execution.sendErrorMessage'), { icon: 'BsInfoCircle' })
      }

      const { successful = [], failures = [] } = resp.data?.bulkExecuteServiceOrders || {}
      const total = successful.length + failures.length

      if (successful.length === 0) {
        return openAlert('Nenhuma atividade pôde ser salva', describeBulkFailures(failures), {
          icon: 'BsInfoCircle'
        })
      }

      if (failures.length > 0) {
        notify.success({ title: `${successful.length} de ${total} atividades salvas com sucesso` })
        openAlert(
          `${failures.length} atividade(s) não puderam ser salvas`,
          describeBulkFailures(failures),
          { icon: 'BsInfoCircle' }
        )
        return onSuccess(resp.data?.bulkExecuteServiceOrders)
      }

      return onSuccess(resp.data?.bulkExecuteServiceOrders)
    })
  }

  const handleRequest = () => {
    if (isBulk) {
      const { entries, invalidRow } = getBulkPayload({ shouldValidate })

      if (!!shouldValidate && invalidRow) {
        const soLabel = invalidRow.serviceOrder?.yearlyId || invalidRow.serviceOrder?.name || `#${invalidRow.serviceOrder?.id}`
        const questionLabel = invalidRow.reply?.question?.name || ''

        return openAlert(
          t('execution.requiredAnswersMissing'),
          `${soLabel} — ${questionLabel}: ${t('execution.requiredAnswersMissingMessage')}`,
          { icon: 'BsInfoCircle' }
        )
      }

      return handleBulkRequest({ entries, events: cleanEventsRequest(form.events, newStatusId) })
    }

    const replies = getRepliesValue()

    const firstInvalidReplyIndex = indexOfFirstInvalidReply(replies)

    if (!!shouldValidate && firstInvalidReplyIndex) {
      setFocus(firstInvalidReplyIndex?.questionId)

      return openAlert(t('execution.requiredAnswersMissing'), t('execution.requiredAnswersMissingMessage'), {
        icon: 'BsInfoCircle'
      })
    }
    const input = {
      replies: cleanRepliesRequest(replies),
      events: cleanEventsRequest(form.events, newStatusId)
    }

    return request({ variables: { id: serviceOrder.id, input, token } }).then(resp => {
      if (resp.errors) {
        handleStockErrors(replies, resp.errors)
        const firstInvalidReplyIndex = indexOfFirstInvalidStockReply(replies)

        setFocus(firstInvalidReplyIndex?.questionId)

        return openAlert(t('execution.sendError'), t('execution.sendErrorMessage'), {
          icon: 'BsInfoCircle'
        })
      } else {
        onSuccess(resp.data?.updateServiceOrder)
      }
    })
  }

  return [handleRequest, isBulk ? bulkState : state]
}
